import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Brain, Zap, ArrowUpDown, Loader2 } from 'lucide-react';
import { glassCard, glassCardStatic } from '@/utils/admin-glass-styles';
import { supabase } from '@/integrations/supabase/client';

interface UserUsageSummary {
  user_id: string;
  user_type: string;
  name: string;
  total_input: number;
  total_output: number;
  total_tokens: number;
  call_count: number;
}

interface FeatureBreakdown {
  feature: string;
  total_input: number;
  total_output: number;
  total_tokens: number;
  call_count: number;
}

const AdminAIUsage = () => {
  const [userSummaries, setUserSummaries] = useState<UserUsageSummary[]>([]);
  const [featureBreakdown, setFeatureBreakdown] = useState<FeatureBreakdown[]>([]);
  const [totalTokens, setTotalTokens] = useState(0);
  const [totalCalls, setTotalCalls] = useState(0);
  const [uniqueUsers, setUniqueUsers] = useState(0);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('30');

  useEffect(() => {
    fetchUsageData();
  }, [timeRange]);

  const fetchUsageData = async () => {
    try {
      setLoading(true);

      const since = new Date();
      since.setDate(since.getDate() - parseInt(timeRange));
      const sinceISO = since.toISOString();

      // Fetch all usage records within range
      const { data: usageRecords, error } = await supabase
        .from('ai_token_usage' as any)
        .select('*')
        .gte('created_at', sinceISO)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching AI usage:', error);
        setLoading(false);
        return;
      }

      const records = (usageRecords || []) as any[];

      // Aggregate by user
      const userMap = new Map<string, UserUsageSummary>();
      const featureMap = new Map<string, FeatureBreakdown>();

      for (const r of records) {
        // User aggregation
        const key = r.user_id;
        if (!userMap.has(key)) {
          userMap.set(key, {
            user_id: r.user_id,
            user_type: r.user_type,
            name: '',
            total_input: 0,
            total_output: 0,
            total_tokens: 0,
            call_count: 0,
          });
        }
        const u = userMap.get(key)!;
        u.total_input += r.input_tokens || 0;
        u.total_output += r.output_tokens || 0;
        u.total_tokens += (r.input_tokens || 0) + (r.output_tokens || 0);
        u.call_count += 1;

        // Feature aggregation
        const feat = r.feature || 'unknown';
        if (!featureMap.has(feat)) {
          featureMap.set(feat, { feature: feat, total_input: 0, total_output: 0, total_tokens: 0, call_count: 0 });
        }
        const f = featureMap.get(feat)!;
        f.total_input += r.input_tokens || 0;
        f.total_output += r.output_tokens || 0;
        f.total_tokens += (r.input_tokens || 0) + (r.output_tokens || 0);
        f.call_count += 1;
      }

      // Resolve user names
      const userIds = Array.from(userMap.keys());
      if (userIds.length > 0) {
        const { data: clients } = await supabase
          .from('clients')
          .select('id, first_name, last_name')
          .in('id', userIds);
        const { data: practitioners } = await supabase
          .from('practitioners')
          .select('id, first_name, last_name')
          .in('id', userIds);

        for (const c of clients || []) {
          const u = userMap.get(c.id);
          if (u) u.name = `${c.first_name || ''} ${c.last_name || ''}`.trim() || c.id.slice(0, 8);
        }
        for (const p of practitioners || []) {
          const u = userMap.get(p.id);
          if (u && !u.name) u.name = `${p.first_name || ''} ${p.last_name || ''}`.trim() || p.id.slice(0, 8);
        }
        // Fallback for unresolved
        for (const u of userMap.values()) {
          if (!u.name) u.name = u.user_id.slice(0, 8) + '...';
        }
      }

      const sortedUsers = Array.from(userMap.values()).sort((a, b) => b.total_tokens - a.total_tokens);
      const sortedFeatures = Array.from(featureMap.values()).sort((a, b) => b.total_tokens - a.total_tokens);

      const grandTotal = records.reduce((sum, r) => sum + (r.input_tokens || 0) + (r.output_tokens || 0), 0);

      setUserSummaries(sortedUsers);
      setFeatureBreakdown(sortedFeatures);
      setTotalTokens(grandTotal);
      setTotalCalls(records.length);
      setUniqueUsers(userMap.size);
    } catch (err) {
      console.error('Error in fetchUsageData:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (n: number) => n.toLocaleString();

  const statCards = [
    { title: 'Total Tokens', value: formatNumber(totalTokens), icon: Zap, color: 'text-amber-600' },
    { title: 'Total Calls', value: formatNumber(totalCalls), icon: ArrowUpDown, color: 'text-blue-600' },
    { title: 'Unique Users', value: formatNumber(uniqueUsers), icon: Brain, color: 'text-purple-600' },
    { title: 'Avg Tokens/Call', value: totalCalls > 0 ? formatNumber(Math.round(totalTokens / totalCalls)) : '0', icon: Zap, color: 'text-green-600' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">AI Usage Traceboard</h1>
        <Select value={timeRange} onValueChange={setTimeRange}>
          <SelectTrigger className="w-[160px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
            <SelectItem value="365">Last year</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map(card => (
          <Card key={card.title} className={glassCard}>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">{card.title}</CardTitle>
              <card.icon className={`h-5 w-5 ${card.color}`} />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{card.value}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Feature Breakdown */}
      <Card className={glassCardStatic}>
        <CardHeader>
          <CardTitle className="text-lg">Usage by Feature</CardTitle>
        </CardHeader>
        <CardContent>
          {featureBreakdown.length === 0 ? (
            <p className="text-muted-foreground text-sm py-4 text-center">No AI usage data yet.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Feature</TableHead>
                  <TableHead className="text-right">Calls</TableHead>
                  <TableHead className="text-right">Input Tokens</TableHead>
                  <TableHead className="text-right">Output Tokens</TableHead>
                  <TableHead className="text-right">Total Tokens</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {featureBreakdown.map(f => (
                  <TableRow key={f.feature}>
                    <TableCell className="font-medium capitalize">{f.feature.replace(/_/g, ' ')}</TableCell>
                    <TableCell className="text-right">{formatNumber(f.call_count)}</TableCell>
                    <TableCell className="text-right">{formatNumber(f.total_input)}</TableCell>
                    <TableCell className="text-right">{formatNumber(f.total_output)}</TableCell>
                    <TableCell className="text-right font-semibold">{formatNumber(f.total_tokens)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Per-User Table */}
      <Card className={glassCardStatic}>
        <CardHeader>
          <CardTitle className="text-lg">Usage by User</CardTitle>
        </CardHeader>
        <CardContent>
          {userSummaries.length === 0 ? (
            <p className="text-muted-foreground text-sm py-4 text-center">No AI usage data yet.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className="text-right">Calls</TableHead>
                  <TableHead className="text-right">Input Tokens</TableHead>
                  <TableHead className="text-right">Output Tokens</TableHead>
                  <TableHead className="text-right">Total Tokens</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {userSummaries.map(u => (
                  <TableRow key={u.user_id}>
                    <TableCell className="font-medium">{u.name}</TableCell>
                    <TableCell className="capitalize">{u.user_type}</TableCell>
                    <TableCell className="text-right">{formatNumber(u.call_count)}</TableCell>
                    <TableCell className="text-right">{formatNumber(u.total_input)}</TableCell>
                    <TableCell className="text-right">{formatNumber(u.total_output)}</TableCell>
                    <TableCell className="text-right font-semibold">{formatNumber(u.total_tokens)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminAIUsage;
